import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';

// A multi-select dropdown: a button that opens a checkbox list. Empty means
// "any" (no condition).

type MultiCheckProps<T extends string> = {
  label: string;
  options: readonly T[];
  labels?: Partial<Record<T, string>>;
  value: readonly T[];
  onChange: (next: T[]) => void;
  testId: string;
};

export function MultiCheck<T extends string>({ label, options, labels, value, onChange, testId }: MultiCheckProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const name = (o: T) => labels?.[o] ?? o;
  const toggle = (o: T) => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  const summary = value.length === 0 ? 'Any' : value.length === 1 ? name(value[0]) : `${value.length} selected`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'h-8 inline-flex items-center gap-1.5 rounded-md border px-2.5 text-xs bg-white',
          value.length ? 'border-blue-300 text-blue-800' : 'border-gray-200 text-gray-700',
        )}
      >
        <span className="text-gray-500">{label}:</span>
        <span className="font-medium max-w-[10rem] truncate">{summary}</span>
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        // z-40: above Bill Runs' sticky action bar (z-20), which sits just
        // below the filters and otherwise cut the open list in half; below
        // dialogs (z-50).
        <div role="listbox" aria-multiselectable className="absolute z-40 mt-1 min-w-[12rem] max-h-72 overflow-auto rounded-md border border-gray-200 bg-white p-1 shadow-lg">
          {options.length === 0 && <p className="px-2 py-1.5 text-xs text-gray-400">No options</p>}
          {options.map((o) => (
            <label key={o} className="flex items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={value.includes(o)} onChange={() => toggle(o)} data-testid={`${testId}-option`} />
              {name(o)}
            </label>
          ))}
          {value.length > 0 && (
            <button type="button" className="w-full text-left px-2 py-1.5 text-xs text-blue-600 hover:bg-gray-50" onClick={() => onChange([])}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
